import { Howl, Howler } from 'howler';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { IS_UPDATE_VERSION_3_PROJECT } from '@/helper/const';

const API_BASE_PATH = process.env.REACT_APP_DOMAIN_API;

const PLAYLIST = [
  `${API_BASE_PATH}src/uploads/1.mp3`,
  `${API_BASE_PATH}src/uploads/2.mp3`,
  `${API_BASE_PATH}src/uploads/3.mp3`,
];

const STORAGE_KEY = 'sound_isOn';

function getRandomTrack(list, excludeSrc) {
  if (list.length === 1) return list[0];
  let next;
  do {
    next = list[Math.floor(Math.random() * list.length)];
  } while (next === excludeSrc);
  return next;
}

const SoundContext = createContext(null);

export function SoundProvider({ children, volume = 0.6, defaultOn = true, isLoggedIn = false }) {
  const soundRef = useRef(null);
  const currentSrcRef = useRef(null);
  const unlockedRef = useRef(false);

  const [isOn, setIsOnState] = useState(() => {
    if (typeof window === 'undefined') return defaultOn;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === null ? defaultOn : saved === 'true';
  });
  const [isReady, setIsReady] = useState(false);

  const canPlay = isOn;

  const setIsOn = useCallback((value) => {
    setIsOnState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      window.localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const loadTrack = useCallback(
    (autoplay = false) => {
      soundRef.current?.unload();
      setIsReady(false);

      const src = getRandomTrack(PLAYLIST, currentSrcRef.current);
      currentSrcRef.current = src;

      const sound = new Howl({
        src: [src],
        html5: true,
        volume,
        onload: () => {
          setIsReady(true);
          if (autoplay) sound.play();
        },
        onend: () => loadTrack(true),
        onloaderror: (id, err) => console.error('Lỗi tải nhạc:', src, err),
      });

      soundRef.current = sound;
    },
    [volume]
  );

  useEffect(() => {
    if (!IS_UPDATE_VERSION_3_PROJECT) return;

    loadTrack(canPlay);
    return () => soundRef.current?.unload();
  }, []);

  useEffect(() => {
    if (!IS_UPDATE_VERSION_3_PROJECT) return;
    soundRef.current?.volume(volume);
  }, [volume]);

  useEffect(() => {
    if (!IS_UPDATE_VERSION_3_PROJECT) return;

    const sound = soundRef.current;
    if (!sound || !isReady) return;

    if (canPlay) {
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        Howler.ctx.resume();
      }
      if (!sound.playing()) {
        sound.play();
      }
    } else {
      sound.pause();
    }
  }, [canPlay, isReady]);

  useEffect(() => {
    if (!IS_UPDATE_VERSION_3_PROJECT) return;
    if (!canPlay || unlockedRef.current) return;

    const tryPlay = () => {
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        Howler.ctx.resume();
      }

      const sound = soundRef.current;
      if (sound && isReady && !sound.playing()) {
        sound.play();
        unlockedRef.current = true;
        document.removeEventListener('click', tryPlay);
        document.removeEventListener('touchstart', tryPlay);
      }
    };

    document.addEventListener('click', tryPlay);
    document.addEventListener('touchstart', tryPlay);

    return () => {
      document.removeEventListener('click', tryPlay);
      document.removeEventListener('touchstart', tryPlay);
    };
  }, [canPlay, isReady]);

  useEffect(() => {
    if (!isLoggedIn) {
      unlockedRef.current = false;
    }
  }, [isLoggedIn]);

  const toggle = useCallback(() => setIsOn((v) => !v), [setIsOn]);

  return (
    <SoundContext.Provider
      value={{
        isOn: IS_UPDATE_VERSION_3_PROJECT ? isOn : false,
        setIsOn: IS_UPDATE_VERSION_3_PROJECT ? setIsOn : () => { },
        toggle: IS_UPDATE_VERSION_3_PROJECT ? toggle : () => { },
        isReady: IS_UPDATE_VERSION_3_PROJECT ? isReady : false,
      }}
    >
      {children}
    </SoundContext.Provider>
  );
}

export const useSound = () => useContext(SoundContext);